import { buildEndpoint } from '@zillow/ab-client';
import type { SharedSession, SharedSessionOptions } from '@zillow/s3s-client';
import s3sClient from '@zillow/s3s-client';

import type { SharedSessionData, URLParams } from '../types/genericTypes';

const SESSION_ITEMS = ['currentGeography', 'geoIpLocation'];
/**
 * Get the latest and most specific regionId from the currentGeography object
 * @param {Object} currentGeography
 * @param {SharedSession['currentGeography']} currentGeography.best geo hierarchy
 * @param {SharedSession['currentGeography']} currentGeography.latest geo hierarchy
 * @returns {Number} regionId
 *
 */
const getRegionId = ({ latest, best }: NonNullable<SharedSession['currentGeography']>) => {
    const { cityId, countyId, stateId, nationId, neighborhoodId } = latest ?? best;
    return neighborhoodId || cityId || countyId || stateId || nationId;
};

/**
 * Get shared session state from endpoint and return the pieces we care about
 * @param {String} guid
 * @param {URLParams} endpoint Config
 * @param {SharedSessionOptions} s3Options additional params to pass to the client
 *
 * @returns {SharedSessionData} with regionId and centroid
 */
const getSharedSessionState = async (
    guid: string,
    { protocol, host, port }: URLParams,
    s3sOptions: SharedSessionOptions
): Promise<SharedSessionData> => {
    try {
        const client = s3sClient(guid, buildEndpoint(protocol, host, port), s3sOptions);
        const { geoIpLocation, currentGeography } = await client.getSharedSessionState(
            SESSION_ITEMS
        );
        const sessionData: SharedSessionData = {};
        if (geoIpLocation?.latLong) {
            sessionData.centroid = {
                lat: geoIpLocation.latLong.latitude,
                lon: geoIpLocation.latLong.longitude,
            };
        }
        sessionData.zipcode = geoIpLocation?.zipcode;
        sessionData.city = geoIpLocation?.city;
        if (currentGeography) {
            sessionData.regionId = getRegionId(currentGeography);
        }

        return sessionData;
    } catch (e) {
        // let's log but return an object so we don't fail the page.
        /* eslint no-console: 0 */
        console.warn('Failed to get shared session data: ', e);
        return {};
    }
};

export default getSharedSessionState;
